/* HistoryBookSection — kawaii personal history book showing all cinematic
 * walkthroughs as chapters with AI-generated summaries. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Sparkles, Loader2, ChevronRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryBookSection() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/timetravel/cinematic/book/chapters`, { withCredentials: true });
      setChapters(data?.chapters || []);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const generateChapter = async (id) => {
    setGeneratingId(id);
    try {
      const { data } = await axios.post(`${API}/timetravel/cinematic/${id}/chapter`, {}, { withCredentials: true });
      setChapters((prev) => prev.map((c) => c.id === id ? { ...c, chapter_summary: data.chapter_summary } : c));
      toast.success('Kapitel skapat 📖');
    } catch { toast.error('Kunde inte skapa kapitel'); }
    finally { setGeneratingId(null); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="neo-btn w-full py-2 bg-gradient-to-r from-[#FFE8A3] via-[#FFC8DD] to-[#E8E0FF] font-bold text-sm flex items-center justify-center gap-2"
        data-testid="open-history-book">
        <BookOpen className="w-4 h-4" /> Min historiebok 📚
      </button>

      {open && (
        <div className="fixed inset-0 z-[128] bg-black/60 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto"
             onClick={() => setOpen(false)} data-testid="history-book-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-gradient-to-br from-[#FFFAF0] via-[#FFE8F5] to-[#E8F4FF] rounded-3xl p-4 sm:p-6 w-full max-w-3xl my-4 max-h-[92vh] overflow-y-auto neo-card">
            <header className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#D06892]" />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60">Min historiebok</p>
                  <h2 className="font-['Fredoka'] font-bold text-xl">Mina tidsresor 📚</h2>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-2 hover:bg-black/5 rounded-full" data-testid="history-book-close">
                <X className="w-5 h-5" />
              </button>
            </header>

            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" /></div>
            ) : chapters.length === 0 ? (
              <div className="text-center py-12" data-testid="history-book-empty">
                <p className="text-5xl mb-2">📖</p>
                <p className="font-bold">Din historiebok är tom än så länge</p>
                <p className="text-xs opacity-60 mt-1">Starta en filmupplevelse så fyller du på kapitlen 🌸</p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="history-book-chapters">
                {chapters.map((ch) => (
                  <li key={ch.id} className="neo-card p-3 bg-white flex gap-3" data-testid={`chapter-${ch.id}`}>
                    {ch.cover ? (
                      <img src={`data:${ch.cover.mime_type};base64,${ch.cover.image_base64}`}
                        alt={ch.title}
                        className="w-24 h-24 sm:w-32 sm:h-32 object-cover rounded-lg border-2 border-black/10 shrink-0 cursor-pointer"
                        onClick={() => setViewing(ch)} />
                    ) : (
                      <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg bg-gradient-to-br from-[#FFE8F5] to-[#A2D2FF] flex items-center justify-center text-4xl shrink-0">
                        {ch.emoji}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase opacity-50 tracking-wide">Kapitel {ch.number}</p>
                      <h4 className="font-['Fredoka'] font-bold text-base leading-tight">{ch.emoji} {ch.title}</h4>
                      <p className="text-[11px] opacity-60">{ch.era} · {ch.location}</p>
                      {ch.chapter_summary ? (
                        <p className="text-xs mt-2 italic leading-relaxed" data-testid={`chapter-summary-${ch.id}`}>
                          {ch.chapter_summary}
                        </p>
                      ) : (
                        <button onClick={() => generateChapter(ch.id)}
                          disabled={generatingId === ch.id}
                          className="neo-btn mt-2 px-3 py-1.5 bg-[#FFC8DD] text-xs font-bold flex items-center gap-1"
                          data-testid={`generate-chapter-${ch.id}`}>
                          {generatingId === ch.id
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Skriver kapitel...</>
                            : <><Sparkles className="w-3 h-3" /> Skapa kapitel-sammanfattning</>}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {chapters.length > 0 && (
              <p className="text-center text-[10px] opacity-50 italic mt-4">
                {chapters.length} {chapters.length === 1 ? 'kapitel' : 'kapitel'} · Klicka på omslagsbilden för stor vy
              </p>
            )}
          </div>
        </div>
      )}

      {/* Cover viewer */}
      {viewing && viewing.cover && (
        <div className="fixed inset-0 z-[135] bg-black/80 flex items-center justify-center p-3"
             onClick={() => setViewing(null)} data-testid="cover-viewer">
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full">
            <img src={`data:${viewing.cover.mime_type};base64,${viewing.cover.image_base64}`}
              alt={viewing.title} className="w-full max-h-[85vh] object-contain rounded-lg" />
            <div className="text-white text-center mt-2">
              <p className="text-[10px] uppercase opacity-60">Kapitel {viewing.number} · {viewing.era}</p>
              <h4 className="font-['Fredoka'] font-bold text-lg">{viewing.emoji} {viewing.title}</h4>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
